import Layout from './components/Layout.js';
import Support from './pages/Support.js';
import Success from './pages/Success.js';
import Error from './pages/Error.js';
import Settings from './pages/Settings.js';
import { ErrorBoundary } from 'react-error-boundary';
import { useEffect } from 'react';
import {
  NotFoundRoute,
  ParsedLocation,
  RouterProvider,
  createRootRouteWithContext,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { createRoute, createRouter } from '@tanstack/react-router';
import { routerWithQueryClient } from '@tanstack/react-router-with-query';
import { Auth0ContextInterface, useAuth0, User } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Loader from './pages/Loader.js';
import Workouts from './components/Workouts.js';
import WorkoutDetail from './pages/WorkoutDetail.js';
import LogWorkout from './pages/LogWorkout.js';
import PlannedWorkoutDetail from './pages/PlannedWorkoutDetail.js';
import PlannedWorkoutForm from './pages/PlannedWorkoutForm.js';
import Logout from './components/Logout.js';
import PlanPage from './pages/Plan.js';
import Login from './components/Login.js';
import Signup from './components/Signup.js';
import Checkout from './pages/Checkout.js';
import Callback from './pages/Callback.js';
import plausible from './plausible.js';
import Admin from './pages/Admin.js';
import Races from './pages/Races.js';
import RaceDetail from './pages/RaceDetail.js';
import Dashboard from './pages/Dashboard.js';
import IntakeForm from './components/Intake.js';
import { useProfile } from './hooks/useProfile.js';
import SharedWorkout from './pages/SharedWorkout.js';
import SharedPlannedWorkout from './pages/SharedPlannedWorkout.js';

// Error fallback component for react-error-boundary
function ErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  // Show error toast
  const showErrorToast = () => {
    const event = new CustomEvent('showToast', {
      detail: {
        message: 'Something went wrong. The page has been reset.',
        type: 'error',
      },
    });
    window.dispatchEvent(event);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="surface-card w-full max-w-md p-7 text-center sm:p-9">
        <div className="text-red-600 mb-4">
          <svg
            className="w-16 h-16 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-brand-midnight mb-2">
          Something went wrong
        </h2>
        <p className="text-gray-600 mb-6">
          We encountered an unexpected error. Don't worry, your data is safe.
        </p>
        <div className="space-y-3">
          <button
            onClick={() => {
              resetErrorBoundary();
              showErrorToast();
            }}
            className="w-full rounded-full bg-brand-volt px-4 py-3 font-bold text-brand-midnight transition-colors hover:bg-brand-volt/90"
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="w-full rounded-full bg-brand-midnight px-4 py-3 font-bold text-white transition-colors hover:bg-brand-slate"
          >
            Refresh page
          </button>
        </div>
        <details className="mt-4 text-left">
          <summary className="cursor-pointer text-sm text-gray-500 mb-2">Support details</summary>
          <p className="text-xs text-gray-500">Share this reference with support if the problem continues: {error.name}</p>
        </details>
      </div>
    </div>
  );
}

// Auth wrapper component that protects routes requiring authentication
const AuthPage = ({
  children,
  allowUnpaid = false,
  redirectPaidTo,
}: {
  children: React.ReactNode;
  allowUnpaid?: boolean;
  redirectPaidTo?: '/dashboard';
}) => {
  const { isAuthenticated, isLoading, error } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && (!isAuthenticated || error)) {
      navigate({ to: '/login' });
    }
  }, [error, isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return <Loader />;
  }

  if (!isAuthenticated || error) {
    return <Loader />;
  }

  return (
    <SubscriberPage allowUnpaid={allowUnpaid} redirectPaidTo={redirectPaidTo}>
      {children}
    </SubscriberPage>
  );
};

const SubscriberPage = ({
  children,
  allowUnpaid,
  redirectPaidTo,
}: {
  children: React.ReactNode;
  allowUnpaid: boolean;
  redirectPaidTo?: '/dashboard';
}) => {
  const profile = useProfile();
  const navigate = useNavigate();

  useEffect(() => {
    // Do not make an access decision while the profile is still hydrating.
    // Paid users otherwise briefly look unpaid and get sent to checkout.
    if (profile.isLoading) {
      return;
    }

    if (!allowUnpaid && !profile.data?.user?.isPaidSubscriber) {
      navigate({ to: '/checkout', replace: true });
    }

    if (redirectPaidTo && profile.data?.user?.isPaidSubscriber) {
      navigate({ to: redirectPaidTo, replace: true });
    }
  }, [
    allowUnpaid,
    navigate,
    profile.isLoading,
    profile.data?.user?.isPaidSubscriber,
    redirectPaidTo,
  ]);

  if (profile.isLoading) {
    return <Loader />;
  }

  if (!allowUnpaid && !profile.data?.user?.isPaidSubscriber) {
    return <Loader />;
  }

  if (redirectPaidTo && profile.data?.user?.isPaidSubscriber) {
    return <Loader />;
  }

  return <>{children}</>;
};

// Create the route configuration
const rootRoute = createRootRouteWithContext<{
  auth: Auth0ContextInterface<User> | undefined;
  queryClient: QueryClient;
}>()({
  component: Layout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    return redirect({ to: '/dashboard' });
  },
});

const successRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'success',
  component: Success,
});

const errorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'error',
  component: Error,
});

const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'support',
  component: Support,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login',
  component: Login,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'signup',
  component: Signup,
});

const checkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'checkout',
  beforeLoad: async ({ context }) => {
    await context.queryClient.resetQueries({ queryKey: ['profile'] });
  },
  component: () => (
    <AuthPage allowUnpaid redirectPaidTo="/dashboard">
      <Checkout />
    </AuthPage>
  ),
});

const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'logout',
  component: Logout,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'callback',
  component: Callback,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'dashboard',
  component: () => (
    <AuthPage>
      <Dashboard />
    </AuthPage>
  ),
});

const planRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'plan',
  component: () => (
    <AuthPage>
      <PlanPage />
    </AuthPage>
  ),
});

const createPlanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'plan/create',
  component: () => (
    <AuthPage>
      <IntakeForm />
    </AuthPage>
  ),
});

const plannedWorkoutNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'plan/workout/new',
  component: () => (
    <AuthPage>
      <PlannedWorkoutForm mode="generate" />
    </AuthPage>
  ),
});

const plannedWorkoutManualRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'plan/workout/new/manual',
  component: () => (
    <AuthPage>
      <PlannedWorkoutForm mode="manual" />
    </AuthPage>
  ),
});

const plannedWorkoutEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'plan/workout/$id/edit',
  component: () => (
    <AuthPage>
      <PlannedWorkoutForm mode="edit" />
    </AuthPage>
  ),
});

const plannedWorkoutDuplicateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'plan/workout/$id/duplicate',
  component: () => (
    <AuthPage>
      <PlannedWorkoutForm mode="duplicate" />
    </AuthPage>
  ),
});

const plannedWorkoutDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'plan/workout/$id',
  component: () => (
    <AuthPage>
      <PlannedWorkoutDetail />
    </AuthPage>
  ),
});

const racesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'races',
  component: () => (
    <AuthPage>
      <Races />
    </AuthPage>
  ),
});

const raceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'races/$id',
  component: () => (
    <AuthPage>
      <RaceDetail />
    </AuthPage>
  ),
});

const workoutsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'workouts',
  component: () => (
    <AuthPage>
      <Workouts />
    </AuthPage>
  ),
});

const workoutLogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'workouts/log',
  component: () => (
    <AuthPage>
      <LogWorkout />
    </AuthPage>
  ),
});

const workoutDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'workouts/$id',
  component: () => (
    <AuthPage>
      <WorkoutDetail />
    </AuthPage>
  ),
});

const sharedWorkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'shared/workouts/$token',
  component: SharedWorkout,
});

const sharedPlannedWorkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'shared/planned-workouts/$token',
  component: SharedPlannedWorkout,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'settings',
  component: () => (
    <AuthPage>
      <Settings />
    </AuthPage>
  ),
});

const notFoundRoute = new NotFoundRoute({
  getParentRoute: () => rootRoute,
  beforeLoad: async () => {
    return redirect({ to: '/' });
  },
});

// admin route
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'admin',
  validateSearch: (search: Record<string, unknown>): { tab?: string } => {
    return {
      tab: (search?.tab as string) || 'surveys',
    };
  },
  component: () => (
    <AuthPage>
      <Admin />
    </AuthPage>
  ),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  successRoute,
  errorRoute,
  supportRoute,
  loginRoute,
  signupRoute,
  checkoutRoute,
  logoutRoute,
  callbackRoute,
  dashboardRoute,
  planRoute,
  racesRoute,
  raceDetailRoute,
  workoutsRoute,
  workoutLogRoute,
  workoutDetailRoute,
  sharedWorkoutRoute,
  sharedPlannedWorkoutRoute,
  createPlanRoute,
  plannedWorkoutNewRoute,
  plannedWorkoutManualRoute,
  plannedWorkoutEditRoute,
  plannedWorkoutDuplicateRoute,
  plannedWorkoutDetailRoute,
  settingsRoute,
  adminRoute,
  notFoundRoute,
]);

const queryClient = new QueryClient();

const router = routerWithQueryClient(
  createRouter({
    routeTree,
    defaultPreload: 'intent',
    context: {
      auth: undefined,
      queryClient,
    },
    defaultPendingComponent: Loader,
    Wrap: ({ children }: { children: React.ReactNode }) => (
      <>
        <QueryClientProvider client={queryClient}>
          {children}
        </QueryClientProvider>
      </>
    ),
  }),
  queryClient,
);

// setup plausible route tracking
router.subscribe('onLoad', ({ toLocation }: { toLocation: ParsedLocation }) => {
  plausible.trackPageview({
    url: toLocation.pathname,
  });
});

function App() {
  const auth = useAuth0();

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        console.error('App Error Boundary caught an error:', error, errorInfo);
      }}
      onReset={() => {
        // Clear any error state if needed
        window.location.hash = '';
      }}
    >
      <RouterProvider router={router} context={{ auth }} />
    </ErrorBoundary>
  );
}

export function PublicApp() {
  return <RouterProvider router={router} context={{ auth: undefined }} />;
}

export default App;
